import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import { growthbook } from 'src/pages/_app';
import { getBalance } from '@/common/apis/services/wallet/getBalance';
import { removeBook } from '@/common/apis/services/booking/removeBook';
import { fetchMyBookings } from '@/modules/chat/userDataBridge';
import { fetchSuggestion } from './api';
import { sendChatRecoveryEvent } from './events';
import { extractBalance, pickBookingForBook, pollUntil } from './utils';
import {
  initialRecoveryState,
  recoveryReducer,
  RecoveryState,
} from './reducer';

/**
 * Feature flag — doctor-by-doctor rollout (design §11). Default off in GrowthBook:
 * until a rule enables a doctor, nobody sees anything (merge-safe by construction).
 */
export const CHAT_RECOVERY_FLAG = 'chat-recovery:online-visit|enabled';

const REFUND_POLL = { tries: 12, intervalMs: 5000 };

/** The slice of yara's BookingSummary the cancel path needs. */
type BookingSummaryLite = Pick<
  import('@/modules/chat/userDataBridge').BookingSummary,
  'bookId' | 'centerId' | 'referenceCode' | 'nationalCode'
>;

export interface UseChatRecoveryResult {
  state: RecoveryState;
  flagOn: boolean;
  openConfirm: () => void;
  backFromConfirm: () => void;
  confirmSwitch: () => Promise<void>;
  dismiss: () => void;
  /** True while the confirm sheet should render (used by the UI). */
  switching: boolean;
}

export function useChatRecovery(chatId: string | null, active: boolean): UseChatRecoveryResult {
  const flagOn = useFeatureIsOn(CHAT_RECOVERY_FLAG);
  const [state, dispatch] = useReducer(recoveryReducer, initialRecoveryState);
  const fetchedChatRef = useRef<string | null>(null);
  const impressionSentRef = useRef<string | null>(null);

  const baseEvent = useMemo(
    () => ({
      chat_id: chatId,
      book_id: state.data.bookId,
      doctor_user_id: state.data.doctor?.user_id,
      candidate_user_center_id: state.data.candidate?.user_center_id,
      candidate_slug: state.data.candidate?.slug,
      price_current: state.data.candidate?.price_current,
      price_candidate: state.data.candidate?.price_candidate,
      minutes_overdue: state.data.minutesOverdue,
      surface: 'web_chat',
    }),
    [chatId, state.data]
  );

  // One fetch per chat per session: the server owns eligibility/voids, we never
  // re-offer on our own.
  useEffect(() => {
    if (!active || !chatId || !flagOn) return;
    if (fetchedChatRef.current === chatId) return;
    if (fetchedChatRef.current != null) dispatch({ type: 'RESET' });
    fetchedChatRef.current = chatId;
    let cancelled = false;
    (async () => {
      const res = await fetchSuggestion(chatId);
      if (cancelled) return;
      const result = res?.result;
      if (!result || result.state !== 'offered' || !result.candidate || !result.book || !result.doctor) {
        dispatch({ type: 'FETCH_EMPTY' });
        return;
      }
      // Per-doctor targeting: the flag rule matches on doctor_user_id (design §11).
      growthbook.setAttributes({
        ...growthbook.getAttributes(),
        doctor_user_id: result.doctor.user_id,
      });
      dispatch({
        type: 'FETCH_OK',
        book: result.book,
        doctor: result.doctor,
        candidate: result.candidate,
        minutesOverdue: result.minutes_overdue,
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [active, chatId, flagOn]);

  // Impression fires once the dock is actually rendered.
  useEffect(() => {
    if (state.phase !== 'offered' || !state.data.bookId) return;
    if (impressionSentRef.current === state.data.bookId) return;
    impressionSentRef.current = state.data.bookId;
    sendChatRecoveryEvent('impression', { ...baseEvent, flag: CHAT_RECOVERY_FLAG });
  }, [state.phase, state.data.bookId, baseEvent]);

  const openConfirm = useCallback(() => {
    sendChatRecoveryEvent('switch_clicked', baseEvent);
    dispatch({ type: 'OPEN_CONFIRM' });
    sendChatRecoveryEvent('confirm_viewed', baseEvent);
  }, [baseEvent]);

  const dismiss = useCallback(() => {
    sendChatRecoveryEvent('dismiss', baseEvent);
    dispatch({ type: 'DISMISS' });
  }, [baseEvent]);

  const backFromConfirm = useCallback(() => {
    sendChatRecoveryEvent('confirm_dismissed', baseEvent);
    dispatch({ type: 'CONFIRM_DISMISS' });
  }, [baseEvent]);

  const confirmSwitch = useCallback(async () => {
    sendChatRecoveryEvent('confirmed', baseEvent);
    const bookId = state.data.bookId;
    if (!bookId) return;

    // Resolve the booking from the user's own list (yara's fetch + guard): only a
    // booking present in this very session's fetched list may be cancelled.
    let booking: BookingSummaryLite | null = null;
    try {
      const all = await fetchMyBookings();
      booking = pickBookingForBook(all, bookId);
    } catch {
      booking = null;
    }

    if (!booking || booking.centerId !== '5532') {
      sendChatRecoveryEvent('switch_abandoned', { ...baseEvent, reason: 'booking_not_resolved' });
      dispatch({ type: 'FAIL_BACK' });
      return;
    }

    // Cancel with the patient's own session via the very same service myTurn uses.
    try {
      const res: any = await removeBook(
        {
          center_id: booking.centerId,
          reference_code: booking.referenceCode,
          national_code: booking.nationalCode,
          book_id: booking.bookId,
        },
        false
      );
      const ok = res?.data?.status === 1 || res?.status === 200;
      if (!ok) throw new Error(res?.data?.message || 'cancel failed');
      sendChatRecoveryEvent('cancel_clicked', baseEvent);
      dispatch({ type: 'CANCEL_OK' });
    } catch {
      sendChatRecoveryEvent('switch_abandoned', { ...baseEvent, reason: 'cancel_failed' });
      dispatch({ type: 'CANCEL_FAIL' });
      return;
    }

    // Wait until the refund is visible in the wallet (bounded); proceed regardless —
    // the money is safe either way, katibe may just lag our read.
    const price = Number(state.data.candidate?.price_candidate ?? 0);
    const balance = await pollUntil(
      async () => {
        const res = await getBalance();
        const value = extractBalance(res);
        if (value !== null && (!price || value >= price)) return value;
        return null;
      },
      REFUND_POLL
    );
    dispatch(balance !== null ? { type: 'REFUND_OK' } : { type: 'REFUND_SLOW' });
  }, [baseEvent, state.data.bookId, state.data.candidate?.price_candidate]);

  const switching = state.phase === 'confirming' || state.phase === 'cancelling' || state.phase === 'waiting_refund';

  return { state, flagOn, openConfirm, backFromConfirm, confirmSwitch, dismiss, switching };
}
